'use client';

import React from 'react';
import styles from './NavSection.module.scss';

import { useInView } from 'react-intersection-observer';
import createAnimationArr from '@/src/lib/helpers/createAnimationArr';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import Label from "@/src/components/ui/label/Label";
import NextImage from "@/src/components/ui/nextImage/NextImage";

const heading = 'Navigation Features That Drive Revenue';
const excerpt =
    'Simplify site exploration with smart navigation features that improve user experience and drive measurable sales growth.';

type NavItemProps = {
    image: string;
    alt: string;
    title: string;
    text: string;
};

type NavBlockProps = {
    title: string;
    image: string;
    text: string;
};

const gridItems: NavItemProps[] = [
    {
        image: '/images/themes/stockist/nav/pic-1.webp',
        alt: 'Accordion-Based Mega Menu',
        title: 'Accordion-Based Mega Menu',
        text: 'Compact, multi-level navigation system that expands on click — ideal for organizing complex catalogs into clear, collapsible sections without overwhelming the user.',
    },
    {
        image: '/images/themes/stockist/nav/pic-2.webp',
        alt: 'Mega Menu with Submenus & Banner',
        title: 'Mega Menu with Submenus & Banner',
        text: 'Showcase categories, subcategories, and promotions in one expansive dropdown — complete with banners, featured links, and deep navigation.',
    },
];

const listBlocks: NavBlockProps[] = [
    {
        image: '/images/themes/stockist/nav/pic-3.webp',
        title: 'Icon Addition',
        text: 'Add icons from the theme’s built-in icon pack to visually enhance your multi-row sections and guide users more effectively.',
    },
    {
        image: '/images/themes/stockist/nav/pic-4.webp',
        title: 'Collections Organization',
        text: 'Create and organize your collections in various ways through the mega menu to guide users to key sections of your store.',
    },
    {
        image: '/images/themes/stockist/nav/pic-5.webp',
        title: 'Banner Highlights',
        text: 'Highlight important offers and featured launches through banners placed directly in your mega menu.',
    },
    {
        image: '/images/themes/stockist/nav/pic-6.webp',
        title: 'Easy Customization',
        text: 'Easily manage and customize your mega menu — quickly update collections, banners, and layouts to fit your store’s style.',
    },
];

const NavGridItem: React.FC<NavItemProps> = ({ image, alt, title, text }) => (
    <div className={styles.navSection__item}>
        <div className={styles.navSection__pic}>
            <NextImage
                src={image}
                alt={alt}
                fill
                sizes="(max-width: 480px) 100vw, (max-width: 1280px) 30vw, 60vw"
                loading="lazy"
            />
        </div>
        <div className={styles.navSection__about}>
            <h3 className={styles.navSection__title}>{title}</h3>
            <div className={styles.navSection__text}>{text}</div>
        </div>
    </div>
);

const NavListBlock: React.FC<NavBlockProps> = ({ title, text, image }) => (
    <div className={styles.navSection__block}>
        <div className={styles.navSection__video}>
            <NextImage
                src={image}
                alt={title}
                fill
                sizes="(max-width: 480px) 100vw, (max-width: 1024px) 50vw, 25vw"
                loading="lazy"
            />
        </div>
        <h4 className={styles.navSection__label}>{title}</h4>
        <div className={styles.navSection__desc}>{text}</div>
    </div>
);

const NavSection = () => {

    const { ref: inViewRef2, inView: inView2 } = useInView({
        triggerOnce: true,
        rootMargin: '-250px 0px 0px',
        threshold: 0,
    });

    return (
        <section className={styles.navSection}>
            <Wrapper>
                <div className={styles.navSection__head} ref={inViewRef2}>
                    <Label>Navigation</Label>
                    <h2 className={`heading__2 ${styles.heading} ${inView2 && styles.animate__heading}`}>
                        {createAnimationArr(heading, styles.word__wrapper, 0, 0.01)}
                    </h2>
                    <p className={`excerpt ${styles.excerpt} ${inView2 && styles.animate__excerpt}`}>
                        {createAnimationArr(excerpt, styles.word__wrapper, 0.1, 0.01)}
                    </p>
                </div>

                <div className={styles.navSection__grid}>
                    {gridItems.map((item, idx) => (
                        <NavGridItem key={idx} {...item} />
                    ))}
                </div>

                <div className={styles.navSection__list}>
                    {listBlocks.map((block, idx) => (
                        <NavListBlock key={idx} {...block} />
                    ))}
                </div>
            </Wrapper>
        </section>
    );
};

export default NavSection;
